import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, ShoppingBag } from "lucide-react";

import { useLanguage } from "shared/context/LanguageContext";
import { EQUIPMENT_STORE_ENABLED } from "shared/lib/featureFlags";

/**
 * Equipment-store entry point. Training is opened from its recruitment step.
 */
interface PortalFeatureCardsProps {
  trainingComplete: boolean;
  loading: boolean;
}

const PortalFeatureCards: React.FC<PortalFeatureCardsProps> = ({
  trainingComplete,
  loading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dir } = useLanguage();

  if (loading || !EQUIPMENT_STORE_ENABLED) return null;

  const ChevronEnd = dir === "rtl" ? ChevronLeft : ChevronRight;

  return (
    <div className="grid grid-cols-1 gap-3 mb-4">
      <button
        type="button"
        onClick={() => trainingComplete && navigate("/portal/store")}
        disabled={!trainingComplete}
        className={`group flex items-center gap-4 rounded-xl border bg-white p-4 text-start transition-colors sm:p-5 ${
          trainingComplete
            ? "border-gray-200 hover:border-[#bf0035]/30 hover:bg-[#bf0035]/[0.02]"
            : "cursor-not-allowed border-gray-200 opacity-60"
        }`}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#bf0035]/[0.06] text-[#bf0035]">
          <ShoppingBag className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-base font-bold text-gray-900">
            {t("portal.store.cardTitle")}
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {trainingComplete
              ? t("portal.store.cardSubtitle")
              : t("portal.store.cardLocked")}
          </p>
        </div>
        {trainingComplete ? (
          <ChevronEnd className="h-5 w-5 shrink-0 text-gray-400 transition-colors group-hover:text-[#bf0035]" />
        ) : (
          <Lock className="h-5 w-5 text-gray-400 shrink-0" />
        )}
      </button>
    </div>
  );
};

export default PortalFeatureCards;
